import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  useMutation,
  type UseMutationResult,
  type UseMutationOptions,
} from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { DateTime } from "luxon";
// API and queries
import { useStartPageConfig } from "./startPageConfig";
import {
  useUserInfoQuery,
  usePatchUserMutation,
  usePatchUserNotificationPreferencesMutation,
  useUserSetPasswordMutation,
} from "../apiQueries/userQueries";
// UI
import { notifications } from "@mantine/notifications";
// user tracking
import { sendEvent, PrivacyPolicyAcceptedEvent } from "../userEvents";
import { staticLinks } from "@config/staticLinks";
// types
import type { MightierSupportedLanguage } from "@neuromotion-inc/mightier-ts-sdk";
import type { AccountActivateFormData } from "./accountActivationTypes";

/**
 * COMPLETE SETUP (activate account)
 * - set password
 * - update other user fields (timezone, notification preferences, language)
 */

export const useCompleteSetupMutation = (
  opts: UseMutationOptions<void, unknown, AccountActivateFormData>,
): UseMutationResult<void, unknown, AccountActivateFormData> => {
  const startPageConfig = useStartPageConfig();

  const { data: userInfo } = useUserInfoQuery();

  const { mutateAsync: patchUser } = usePatchUserMutation(userInfo?.id);
  const { mutateAsync: patchUserNotificationPrefs } =
    usePatchUserNotificationPreferencesMutation(userInfo?.id);
  const { mutateAsync: setPasswordForLoggedInUser } =
    useUserSetPasswordMutation();

  return useMutation({
    ...opts,
    mutationKey: ["COMPLETE_ACCOUNT_SETUP", userInfo?.id],
    mutationFn: async (formData: AccountActivateFormData) => {
      if (!userInfo?.id || !startPageConfig) {
        throw new Error("User was not loaded at time of form submission");
      }

      // most important one first
      await setPasswordForLoggedInUser({ password: formData.password });

      const promises: Array<Promise<unknown>> = [
        patchUser({
          timezone: DateTime.now().zoneName,
          language: formData.preferredLanguage,
        }),
      ];

      if (startPageConfig.allowUpdateNotificationPreferences) {
        promises.push(
          patchUserNotificationPrefs({
            is_mail_ok: formData.isMailOk,
            is_sms_ok: formData.isSmsOk,
            is_email_ok: formData.isEmailOk,
          }),
        );
      }

      if (startPageConfig.showTermsConditions) {
        promises.push(
          sendEvent(
            new PrivacyPolicyAcceptedEvent(userInfo.id, { view: "/start" }),
          ),
        );
      }

      await Promise.all(promises);
    },
  });
};

interface CompleteSetupCallbacks {
  onSuccess: () => void;
  onError: (e: unknown) => void;
}

export const useCompleteSetupCallbacks = (): CompleteSetupCallbacks => {
  const { t, i18n } = useTranslation();
  const startPageConfig = useStartPageConfig();
  const { data: userInfo } = useUserInfoQuery();

  // this is what we want - "best fit out of the languages we supplied"
  // https://locize.com/blog/react-i18next/
  const selectedLocale = i18n.resolvedLanguage;

  const parentAppUrl = useMemo(() => {
    return staticLinks.welcome(selectedLocale as MightierSupportedLanguage);
  }, [selectedLocale]);

  const introSurveyUrl = useMemo(() => {
    const metaObj = { user_id: `${userInfo?.account.primary_contact}` };
    const userIdBase64Encoded = btoa(JSON.stringify(metaObj));

    return `/s/aa15ba94-82c2-4a47-ac1c-80a1f15966d8?meta=${userIdBase64Encoded}`;
  }, [userInfo]);

  const onSuccess = useCallback(() => {
    notifications.show({
      title: t("startPage.submitCallbackMessages.successTitle"),
      message: t("startPage.submitCallbackMessages.successMsg"),
      color: "green",
    });

    setTimeout(() => {
      if (startPageConfig?.redirectToIntroSurvey) {
        window.location.assign(introSurveyUrl);
      } else {
        window.location.assign(parentAppUrl);
      }
    }, 1500);
  }, [parentAppUrl, t, introSurveyUrl, startPageConfig]);

  const onError = useCallback(
    (e: unknown) => {
      Sentry.captureMessage("Failed to complete account activation", {
        extra: { error: e },
      });
      notifications.show({
        title: t("startPage.submitCallbackMessages.errorTitle"),
        message: t("startPage.submitCallbackMessages.errorMsg"),
        color: "red",
      });
    },
    [t],
  );

  return useMemo(() => ({ onSuccess, onError }), [onSuccess, onError]);
};
